/* =========================================================================
   TT PLACEMENT RENDER — customer-facing V2 renderer
   Technician Tees · created 2026-08-31 · DEV theme 194825715995

   WHAT THIS IS
   The renderer half of the port. It drives THREE zones —
   logo_chest, name and logo_back — from mock.placement_json_v2 through
   assets/tt-placement.js, so the customer-facing page computes placement
   with the same code as the visual editor.

   IT OWNS EXACTLY THREE ZONES. Everything else is untouched:
     * logo_sleeve      still the section + tt-garment-frame legacy path
     * logo_front_full  still tt-full-front.js  (DEFERRED, not ported)
   Ownership is declared on window.TT_V2_RENDERER_ZONES, which
   tt-garment-frame.js checks before it re-applies a translated v1 value.

   COORDINATE SYSTEM
   tt-placement.js returns rectangles in the 2000 x 2200 garment canvas.
   Those become percentages of #ttV2Garment, whose box IS the garment
   rectangle. That mapping is 1:1 only while the base image is the
   2000 x 2200 flat standard, which is what V2 calibration assumes; a
   non-standard base is reported in the console rather than silently
   rendered wrong.

   THE BOX IS THE INK RECTANGLE, NOT THE IMAGE FILE. TTPlacement.place()
   returns where to put the IMAGE ELEMENT so the visible ink lands in the
   saved box. Because the returned rect carries the artwork's true aspect,
   the existing object-fit:contain on .tt-v2-logo is an exact fill and
   changes nothing.

   GRACEFUL DEGRADATION — deliberate, and matching the frame's philosophy:
   if TTPlacement is missing, if there is no saved v2 payload, or if the
   garment frame has not been built, this file claims NOTHING and returns.
   The previous v1 rendering then stands exactly as before.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__TT_V2_RENDER_BOUND__) return;
  window.__TT_V2_RENDER_BOUND__ = true;

  var CW = 2000, CH = 2200;
  var NAME_FONT = '"Playball", cursive';
  var OWNED = { logo_chest: true, logo_back: true, name: true };

  function readJsonScript(id, fallback) {
    var el = document.getElementById(id);
    if (!el) return fallback;
    try {
      var raw = (el.textContent || '').trim();
      if (!raw) return fallback;
      var parsed = JSON.parse(raw);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return parsed;
    } catch (e) {
      console.warn('TT V2 render: JSON parse failed for', id, e);
      return fallback;
    }
  }

  var cfg = readJsonScript('TT_V2_RENDER_CFG', null);
  var saved = cfg && cfg.saved;
  var handle = (cfg && cfg.handle) || '';

  /* Claim nothing unless we can genuinely take over. This runs while the
     document is still parsing, well before tt-garment-frame.js executes,
     so the frame sees the flag in time. */
  if (!window.TTPlacement) {
    console.warn('TT V2 render: TTPlacement missing — legacy v1 path retained.');
    return;
  }
  if (!saved || typeof saved !== 'object' || !saved.garment) {
    console.warn('TT V2 render: no saved placement_json_v2 garment block for "' +
                 handle + '" — legacy v1 path retained.');
    return;
  }
  window.TT_V2_RENDERER_ZONES = OWNED;

  var report = { handle: handle, zones: {}, font: 'pending', base: null };
  window.TT_V2_RENDER_REPORT = report;

  function garmentHost() { return document.getElementById('ttV2Garment'); }

  function currentView() {
    var a = document.querySelector('[data-tt-v2-view].is-active');
    return a ? a.getAttribute('data-tt-v2-view') : 'front';
  }

  function pctBox(el, fit) {
    el.style.left = (fit.left / CW * 100) + '%';
    el.style.top = (fit.top / CH * 100) + '%';
    el.style.width = (fit.width / CW * 100) + '%';
    el.style.height = (fit.height / CH * 100) + '%';
    el.style.transform = 'none';           /* rect is absolute, not centred */
  }

  /* Ink measurement, with the whole-canvas fallback upgraded to carry the
     element's real dimensions. TTPlacement.FULL_INK has no W/H, which would
     leave fitInk with no aspect information; naturalWidth/naturalHeight are
     readable even when a pixel readback is not. */
  function inkFor(img, url) {
    return window.TTPlacement.measureInk(url).then(function (ink) {
      if (ink && ink.measured) return ink;
      if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
        return { fx: 0, fy: 0, fw: 1, fh: 1,
                 W: img.naturalWidth, H: img.naturalHeight, measured: false };
      }
      return ink;
    });
  }

  var lastChestInkCy = null;

  function placeLogo(zone, elId) {
    var el = document.getElementById(elId);
    if (!el) return Promise.resolve();
    var img = el.querySelector('img');
    var url = img && (img.getAttribute('src') || '');
    if (!url) return Promise.resolve();

    return inkFor(img, url).then(function (ink) {
      var fit = window.TTPlacement.place(zone, saved, ink, handle);
      pctBox(el, fit);
      if (zone === 'logo_chest') lastChestInkCy = fit.inkCy;

      var g = window.TTPlacement.nameGeometry(saved, handle, null);
      report.zones[zone] = {
        source: fit.source,
        measured: !!ink.measured,
        artworkCanvas: (ink.W || 0) + 'x' + (ink.H || 0),
        inkFractions: { fx: +(ink.fx || 0).toFixed(4), fy: +(ink.fy || 0).toFixed(4),
                        fw: +(ink.fw || 1).toFixed(4), fh: +(ink.fh || 1).toFixed(4) },
        savedBoxPx: { cx: +fit.box.cx.toFixed(1), top: +fit.box.top.toFixed(1),
                      w: +fit.box.w.toFixed(1), h: +fit.box.h.toFixed(1) },
        renderedInkPx: { left: +fit.inkLeft.toFixed(1), top: +fit.inkTop.toFixed(1),
                         w: +fit.inkW.toFixed(1), h: +fit.inkH.toFixed(1) },
        renderedInkInches: { w: +(fit.inkW / g.ppi).toFixed(3),
                             h: +(fit.inkH / g.ppi).toFixed(3) },
        elementAspect: +(fit.width / fit.height).toFixed(4),
        artworkAspect: +((ink.W || 1) / (ink.H || 1)).toFixed(4),
        anchor: fit.anchor
      };
    });
  }

  /* ---- employee name ----------------------------------------------------
     Sized by REAL INK measurement to the 0.600in rule, exactly as the editor
     does. The section's viewport-relative font-size:clamp() cannot honour
     that rule, so the span is replaced by a canvas whose box is the measured
     ink rectangle. Width varies naturally; height is always 0.600in. */
  var probe = document.createElement('canvas').getContext('2d');
  var metricsOk = true;

  function measureText(text, size) {
    probe.font = size + 'px ' + NAME_FONT;
    var m = probe.measureText(text);
    if (typeof m.actualBoundingBoxAscent !== 'number' ||
        typeof m.actualBoundingBoxLeft !== 'number') {
      metricsOk = false;
      return { asc: size * 0.75, desc: size * 0.25, left: 0, right: m.width,
               w: m.width, h: size };
    }
    return { asc: m.actualBoundingBoxAscent, desc: m.actualBoundingBoxDescent,
             left: m.actualBoundingBoxLeft, right: m.actualBoundingBoxRight,
             w: m.actualBoundingBoxLeft + m.actualBoundingBoxRight,
             h: m.actualBoundingBoxAscent + m.actualBoundingBoxDescent };
  }

  function placeName() {
    var box = document.getElementById('ttV2NameBox');
    var span = document.getElementById('ttV2NameText');
    if (!box || !span) return;

    /* EMPLOYEE NAME OFF - garment.name_enabled === false. This must HIDE the
       box, not merely return: the section's own text span stays visible at its
       CSS position until the line further down hides it. */
    if (!window.TTPlacement.nameEnabled(saved)) {
      box.style.display = 'none';
      span.style.display = 'none';
      var offCv = box.querySelector('canvas.tt-v2-name-canvas');
      if (offCv && offCv.parentNode) offCv.parentNode.removeChild(offCv);
      report.zones.name = { source: 'disabled by garment.name_enabled' };
      return;
    }

    var text = (span.textContent || '').trim();
    if (!text) return;

    span.style.display = 'none';           /* the canvas replaces it */
    box.style.overflow = 'visible';

    var cv = box.querySelector('canvas.tt-v2-name-canvas');
    if (!cv) {
      cv = document.createElement('canvas');
      cv.className = 'tt-v2-name-canvas';
      cv.style.display = 'block';
      cv.style.width = '100%';
      cv.style.height = '100%';
      box.appendChild(cv);
    }

    var g = window.TTPlacement.nameGeometry(saved, handle, null);
    var targetInkH = g.h;                  /* 0.600in at this garment scale */
    var m0 = measureText(text, 200);
    if (!m0.h) return;
    var fontSize = 200 * (targetInkH / m0.h);
    var m = measureText(text, fontSize);

    var RES = 3;                           /* supersample, as the editor does */
    cv.width = Math.max(1, Math.ceil(m.w * RES));
    cv.height = Math.max(1, Math.ceil(m.h * RES));
    var ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.font = (fontSize * RES) + 'px ' + NAME_FONT;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(text, m.left * RES, m.asc * RES);

    var cx = g.cx;
    var cy = (g.cy !== null && typeof g.cy !== 'undefined') ? g.cy : lastChestInkCy;
    if (cy === null || typeof cy === 'undefined') return;

    box.style.left = ((cx - m.w / 2) / CW * 100) + '%';
    box.style.top = ((cy - m.h / 2) / CH * 100) + '%';
    box.style.width = (m.w / CW * 100) + '%';
    box.style.height = (m.h / CH * 100) + '%';
    box.style.transform = 'none';

    report.zones.name = {
      source: window.TTPlacement.overrideOf(saved, 'name') ? 'override' : 'rule',
      text: text,
      inkHeightInches: +(m.h / g.ppi).toFixed(4),
      inkWidthInches: +(m.w / g.ppi).toFixed(3),
      ruleHeightInches: +(targetInkH / g.ppi).toFixed(4),
      inkCentreY: +cy.toFixed(1),
      chestInkCentreY: lastChestInkCy === null ? null : +lastChestInkCy.toFixed(1),
      centreOffsetInches: lastChestInkCy === null ? null
                          : +((cy - lastChestInkCy) / g.ppi).toFixed(3),
      metricsSupported: metricsOk
    };
  }

  /* ---- orchestration ---------------------------------------------------- */

  function applyAll() {
    if (!garmentHost()) return;            /* frame not built yet — skip */

    var baseImg = document.getElementById('ttV2PreviewImg');
    if (baseImg && baseImg.naturalWidth > 0) {
      report.base = baseImg.naturalWidth + 'x' + baseImg.naturalHeight;
      if (baseImg.naturalWidth !== CW || baseImg.naturalHeight !== CH) {
        console.warn('TT V2 render: base image is ' + report.base +
                     ', not the 2000x2200 flat standard. V2 percentages assume ' +
                     'the standard canvas; this view may not be trustworthy.');
      }
    }

    var view = currentView();
    if (view === 'front') {
      placeLogo('logo_chest', 'ttV2LogoBoxChest').then(placeName);
    } else if (view === 'back') {
      placeLogo('logo_back', 'ttV2LogoBoxBack');
    }
  }

  function schedule() { setTimeout(applyAll, 0); }

  var baseImg = document.getElementById('ttV2PreviewImg');
  if (baseImg) {
    /* setTimeout so we land AFTER tt-garment-frame's own load handler has
       built the wrapper and moved the overlays into it. */
    baseImg.addEventListener('load', schedule);
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[data-tt-v2-view]')) schedule();
  });

  /* Font must be loaded before the name is measured, or we size a fallback
     cursive and the 0.600in rule is met by the wrong glyphs. */
  function noteFont() {
    var ok = false;
    try { ok = document.fonts.check('200px "Playball"'); } catch (e) {}
    report.font = ok ? 'Playball loaded' : 'Playball NOT loaded — fallback cursive';
    if (!ok) console.warn('TT V2 render: ' + report.font);
  }

  if (document.fonts && document.fonts.load) {
    document.fonts.load('200px "Playball"')
      .then(function () { return document.fonts.ready; })
      .then(function () { noteFont(); applyAll(); })
      .catch(function () { noteFont(); applyAll(); });
  } else {
    noteFont();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();
/* === TT_PLACEMENT_RENDER_END === */
